<template>
  <div>
    <!-- Header with Add Button and Filters -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-btn color="primary" @click="$router.push('/master-data/rates/new')">
          <v-icon left>mdi-plus</v-icon>
          Add New Rate
        </v-btn>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          color="secondary"
          outlined
          @click="showHistoryDialog = true"
          class="mr-2"
        >
          <v-icon left>mdi-history</v-icon>
          View History
        </v-btn>
        <v-btn
          color="info"
          outlined
          @click="refreshRates"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="filters.costCategory"
          :items="costCategoryOptions"
          label="Filter by Cost Category"
          clearable
          no-data-text="Not available"
          outlined
          dense
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-menu
          v-model="effectiveDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="filters.effectiveDate"
              label="Filter by Effective Date"
              prepend-icon="mdi-calendar"
              readonly
              outlined
              dense
              clearable
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.effectiveDate"
            @input="effectiveDateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch
          v-model="filters.showInactive"
          label="Show Inactive Rates"
          class="mt-2"
        ></v-switch>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredRates"
          :loading="loading"
          :sort-by="['costCategoryOrder', 'effectiveFrom']"
          :sort-desc="[false, true]"
          class="elevation-1"
          item-key="id"
        >
          <!-- Cost Category with Icon -->
          <template #item.costCategory="{ item }">
            <v-chip
              :color="getCategoryColor(item.costCategory)"
              text-color="white"
              small
            >
              <v-icon left small>{{ getCategoryIcon(item.costCategory) }}</v-icon>
              {{ formatCostCategory(item.costCategory) }}
            </v-chip>
          </template>

          <!-- Category Order -->
          <template #item.costCategoryOrder="{ item }">
            {{ item.costCategoryOrder }}
          </template>

          <!-- Rate with Type -->
          <template #item.rate="{ item }">
            <span class="font-weight-medium">
              {{ formatRate(item.rate, item.rateType) }}
            </span>
          </template>

          <!-- Rate Type -->
          <template #item.rateType="{ item }">
            <v-chip
              :color="item.rateType === 'FIXED' ? 'blue' : 'green'"
              text-color="white"
              x-small
            >
              {{ item.rateType }}
            </v-chip>
          </template>

          <!-- Effective From -->
          <template #item.effectiveFrom="{ item }">
            {{ formatDate(item.effectiveFrom) }}
          </template>

          <!-- Effective To -->
          <template #item.effectiveTo="{ item }">
            {{ formatDate(item.effectiveTo) }}
          </template>

          <!-- Status -->
          <template #item.isActive="{ item }">
            <v-chip
              :color="item.isActive ? 'success' : 'error'"
              text-color="white"
              x-small
            >
              <v-icon left x-small>
                {{ item.isActive ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              {{ item.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="viewRate(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View Details</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="warning"
                  @click="editRate(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit Rate</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDeleteRate(item)"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!item.isActive"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isActive ? 'Delete Rate' : 'Cannot delete inactive rate' }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Rate History Dialog -->
    <v-dialog v-model="showHistoryDialog" max-width="1200px">
      <v-card>
        <v-card-title>
          <span class="headline">Rate History</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showHistoryDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <rate-history-view />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the rate for
          <strong>{{ selectedRate ? formatCostCategory(selectedRate.costCategory) : '' }}</strong>?
          <br><br>
          <v-alert type="warning" dense>
            This action will mark the rate as inactive and cannot be undone.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="red darken-1" text @click="deleteRateConfirmed">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rate Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedRate">
        <v-card-title>
          <span class="headline">Rate Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Cost Category</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="getCategoryColor(selectedRate.costCategory)"
                      text-color="white"
                      small
                    >
                      <v-icon left small>{{ getCategoryIcon(selectedRate.costCategory) }}</v-icon>
                      {{ formatCostCategory(selectedRate.costCategory) }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Rate</v-list-item-title>
                  <v-list-item-subtitle class="text-h6">
                    {{ formatRate(selectedRate.rate, selectedRate.rateType) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Effective From</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDate(selectedRate.effectiveFrom) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Effective To</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDate(selectedRate.effectiveTo) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Status</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="selectedRate.isActive ? 'success' : 'error'"
                      text-color="white"
                      small
                    >
                      {{ selectedRate.isActive ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Created By</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedRate.createdBy }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>Created At</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDateTime(selectedRate.createdAt) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="editRate(selectedRate)">
            Edit Rate
          </v-btn>
          <v-btn text @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import costCategoryMixin from '../../mixins/costCategoryMixin';

export default {
  name: 'RateList',
  mixins: [costCategoryMixin],
  components: {
    RateHistoryView: () => import('./RateHistoryView.vue'),
  },
  data() {
    return {
      headers: [
        {
          text: 'Order',
          value: 'costCategoryOrder',
          sortable: true,
          width: '150px',
        },
        { text: 'Cost Category', value: 'costCategory', sortable: true },
        { text: 'Rate', value: 'rate', sortable: true },
        { text: 'Rate Type', value: 'rateType', sortable: true },
        { text: 'Effective From', value: 'effectiveFrom', sortable: true },
        { text: 'Effective To', value: 'effectiveTo', sortable: true },
        { text: 'Status', value: 'isActive', sortable: true },
        {
          text: 'Actions', value: 'actions', sortable: false, width: '150px',
        },
      ],
      filters: {
        costCategory: null,
        effectiveDate: null,
        showInactive: false,
      },
      effectiveDateMenu: false,
      showHistoryDialog: false,
      deleteDialog: false,
      detailsDialog: false,
      selectedRate: null,

    };
  },
  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    costCategoryOrderMap() {
      return [...this.costCategories]
        .sort((a, b) => {
          const aOrder = Number(a.calculation_order ?? a.order ?? Number.MAX_SAFE_INTEGER);
          const bOrder = Number(b.calculation_order ?? b.order ?? Number.MAX_SAFE_INTEGER);
          return aOrder - bOrder;
        })
        .reduce((orderMap, category) => {
          const categoryCode = category.category_code || category.code;
          if (categoryCode) {
            orderMap[categoryCode] = Number(
              category.calculation_order ?? category.order ?? Number.MAX_SAFE_INTEGER,
            );
          }
          return orderMap;
        }, {});
    },

    rates() {
      return this.allRates;
    },
    loading() {
      return this.$store.state.rateMaster.loading || false;
    },
    filteredRates() {
      let filtered = [...this.rates];

      // Filter by cost category
      if (this.filters.costCategory) {
        filtered = filtered.filter((rate) => rate.costCategory === this.filters.costCategory);
      }

      // Filter by effective date
      if (this.filters.effectiveDate) {
        const filterDate = new Date(this.filters.effectiveDate);
        filtered = filtered.filter((rate) => {
          const effectiveFrom = new Date(rate.effectiveFrom);
          const effectiveTo = new Date(rate.effectiveTo);
          return filterDate >= effectiveFrom && filterDate <= effectiveTo;
        });
      }

      // Filter by active status
      if (!this.filters.showInactive) {
        filtered = filtered.filter((rate) => rate.isActive);
      }

      return filtered.map((rate) => ({
        ...rate,
        costCategoryOrder: this.costCategoryOrderMap[rate.costCategory] ?? Number.MAX_SAFE_INTEGER,
      }));
    },
  },
  async created() {
    await this.fetchRates();
  },
  methods: {
    ...mapActions('rateMaster', ['fetchRates', 'deleteRate']),

    async refreshRates() {
      await this.fetchRates();
      this.$store.dispatch('showSnackbar', {
        message: 'Rates refreshed successfully',
        color: 'success',
      });
    },

    viewRate(rate) {
      this.selectedRate = rate;
      this.detailsDialog = true;
    },

    editRate(rate) {
      this.$router.push(`/master-data/rates/${rate._id}/edit`);
    },

    confirmDeleteRate(rate) {
      this.selectedRate = rate;
      this.deleteDialog = true;
    },

    async deleteRateConfirmed() {
      try {
        await this.deleteRate(this.selectedRate);
        this.deleteDialog = false;
        this.selectedRate = null;
        this.$store.dispatch('showSnackbar', {
          message: 'Rate deleted successfully',
          color: 'success',
        });
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error deleting rate: ${error.message}`,
          color: 'error',
        });
      }
    },

    formatRate(rate, rateType) {
      if (rateType === 'PERCENTAGE') {
        return `${rate}%`;
      }
      return this.$formatCurrency(rate);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>
